import { useEffect, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { ARTIST, assetUrl, siteImg } from "@/lib/constants";
import { scrollToId } from "@/lib/smooth";
import { ArtFrame, Bindi, FishMotif, LotusMotif } from "@/components/motifs";
import { HERO } from "@/content/chapters";
import { artworkAlt } from "@/lib/site";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`-mb-[0.14em] block overflow-hidden pb-[0.14em] ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ featured = [], statement }) => {
  const [index, setIndex] = useState(0);
  const { scrollY } = useScroll();
  const yArt = useTransform(scrollY, [0, 700], [0, 90]);
  const yLogo = useTransform(scrollY, [0, 700], [0, -60]);

  useEffect(() => {
    if (featured.length < 2) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % featured.length), 7000);
    return () => clearInterval(t);
  }, [featured.length]);

  const current = featured[index];

  return (
    <section id="home" className="relative overflow-hidden pt-28 sm:pt-32" data-testid="hero-section">
      <FishMotif className="pointer-events-none absolute -left-10 bottom-10 w-64 text-foreground/[0.05]" />
      <LotusMotif className="pointer-events-none absolute right-[42%] top-24 hidden w-24 text-foreground/[0.06] lg:block" />

      <div className="mx-auto grid max-w-7xl gap-16 px-5 pb-20 sm:px-8 lg:grid-cols-12 lg:gap-10">
        <div className="flex flex-col justify-center lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mb-8 flex items-center gap-3"
          >
            <Bindi size={9} pulse />
            <p className="eyebrow text-muted-foreground">Mithila · Madhubani · {ARTIST.location}</p>
          </motion.div>

          <h1
            className="font-serif text-4xl font-semibold leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl"
            data-testid="hero-artist-name"
          >
            <MaskedLine delay={0.25}>Dr Piyush</MaskedLine>
            <MaskedLine delay={0.38}>Kiran Nayak</MaskedLine>
          </h1>

          <MaskedLine delay={0.55} className="mt-6 max-w-xl">
            <span className="font-serif text-xl italic leading-snug text-muted-foreground sm:text-2xl">
              {statement || HERO.statement}
            </span>
          </MaskedLine>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-6 max-w-lg text-sm leading-relaxed text-muted-foreground sm:text-base"
          >
            {HERO.welcome}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("gallery")}
              data-testid="hero-enter-gallery-button"
              className="group flex items-center gap-3 rounded-full bg-foreground px-7 py-3.5 text-[13px] font-semibold tracking-wide text-background transition-colors duration-300 hover:bg-accent hover:text-accent-foreground"
            >
              Enter the Gallery
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToId("artist")}
              data-testid="hero-artist-journey-button"
              className="rounded-full border border-foreground/30 px-7 py-3.5 text-[13px] font-semibold tracking-wide transition-colors duration-300 hover:border-accent hover:text-accent"
            >
              My Journey
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-14 flex items-center gap-3 text-muted-foreground"
          >
            <ArrowDown size={14} className="animate-bounce" style={{ animationDuration: "2.4s" }} />
            <span className="eyebrow">Original works · Museum notes · Living tradition</span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y: yLogo }} className="absolute -top-6 right-0 z-20 hidden sm:block">
            <div className="relative">
              <svg viewBox="0 0 120 120" className="animate-spin-slow absolute -inset-4 h-[calc(100%+2rem)] w-[calc(100%+2rem)] text-foreground/25">
                <circle cx="60" cy="60" r="57" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="2 7" />
              </svg>
              <img
                src={siteImg("/assets/logo.png")}
                alt={`${ARTIST.name} — Mithila logo with red bindi`}
                className="h-32 w-32 rounded-full object-cover shadow-xl ring-1 ring-foreground/15 lg:h-36 lg:w-36"
              />
            </div>
          </motion.div>

          <motion.div
            style={{ y: yArt }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-14 sm:mt-16"
          >
            <ArtFrame>
              <div className="relative overflow-hidden bg-paper">
                <motion.img
                  src={siteImg("/assets/artist-hero.jpg")}
                  alt="Dr Piyush Kiran Nayak at her desk, framed by Mithila trees, lotuses and her logo"
                  initial={{ clipPath: "inset(0 0 100% 0)", scale: 1.03 }}
                  animate={{ clipPath: "inset(0 0 0% 0)", scale: 1 }}
                  transition={{ duration: 1.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="block h-auto w-full"
                  data-testid="hero-artist-image"
                />
              </div>
            </ArtFrame>
            <div className="mt-9 flex items-baseline justify-between">
              <p className="font-serif text-lg italic">In the studio, among her motifs</p>
              <span className="eyebrow flex items-center gap-2 text-muted-foreground">
                <Bindi size={6} /> The artist
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="absolute -left-8 bottom-24 z-10 hidden w-40 lg:block xl:w-48"
            data-testid="hero-featured-card"
          >
            <div className="border border-foreground/25 bg-background p-2 shadow-2xl">
              <AnimatePresence mode="wait">
                {current && (
                  <motion.img
                    key={current.id}
                    src={assetUrl(current.image)}
                    alt={artworkAlt(current)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.9 }}
                    className="block h-auto w-full"
                  />
                )}
              </AnimatePresence>
              <AnimatePresence mode="wait">
                <motion.p
                  key={current?.id || "none"}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="px-1 pb-0.5 pt-2 font-mono text-[9px] uppercase leading-relaxed tracking-[0.16em] text-muted-foreground"
                >
                  {current ? `Featured — ${current.title}` : ""}
                </motion.p>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
